import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const RulesWidget: React.FC = () => {
  const [open, setOpen] = useState(false);
  const modalId = useId();
  const titleId = useId();
  const descId = useId();
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);

    // Focus the close button for quick keyboard dismissal.
    queueMicrotask(() => closeBtnRef.current?.focus());

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  return (
    <>
      <button
        className="btn btn-ghost btn-sm rules-trigger"
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={modalId}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
          <path
            d="M9.6 9.4a2.5 2.5 0 0 1 4.9.6c0 1.6-2.5 2-2.5 3.5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <circle cx="12" cy="17" r="1" fill="currentColor" />
        </svg>
        <span>How to play</span>
      </button>

      {/* Portal: the sticky header uses backdrop-filter, which would otherwise trap a fixed overlay. */}
      {open && createPortal(
        <div
          className="modal-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div
            className="modal rules-modal"
            id={modalId}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={descId}
          >
            <div className="modal-header">
              <h2 id={titleId}>How to play Songle</h2>
              <button
                ref={closeBtnRef}
                className="btn btn-ghost btn-icon"
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close rules"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <div id={descId} className="modal-body">
              <p className="rules-lead">
                Songle is a multiplayer song-guessing game. Everyone picks a song, then you race to
                guess other players’ songs as fast as you can.
              </p>

              <ol className="rules-steps">
                <li>
                  <span className="rules-step-num">1</span>
                  <div>
                    <strong>Create or join a room.</strong> Enter a username, then create a new room
                    or join with an invite link / room code.
                  </div>
                </li>
                <li>
                  <span className="rules-step-num">2</span>
                  <div>
                    <strong>Wait for the host to start.</strong> Once at least 2 players are in, the
                    room creator can start the game (optionally with a theme).
                  </div>
                </li>
                <li>
                  <span className="rules-step-num">3</span>
                  <div>
                    <strong>Select your song.</strong> Search for a song or paste a YouTube link,
                    preview it, then confirm your selection. You can trim the title so it only
                    contains the song name.
                  </div>
                </li>
                <li>
                  <span className="rules-step-num">4</span>
                  <div>
                    <strong>Guess in real time.</strong> When the round starts, listen and submit
                    guesses in the chat. After 30 seconds the first letter of every word is revealed.
                  </div>
                </li>
                <li>
                  <span className="rules-step-num">5</span>
                  <div>
                    <strong>Score points.</strong> The first correct guess earns the most points and
                    later ones earn less. When your own song is playing you get 50 points for every
                    player who guesses it. Highest total at the end wins.
                  </div>
                </li>
              </ol>

              <div className="rules-tip">
                <strong>Tip:</strong> if a theme is set and you think a song doesn’t fit, you can
                vote “Doesn’t fit theme” during the round. If enough players agree, the picker
                loses their bonus.
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-primary" onClick={() => setOpen(false)}>
                Got it
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default RulesWidget;
